// The `letters` structure symbol set: card 18 of mockup round 054, "LETTER
// MARKS" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// One bold letter per kind in the manner of a chart's abbreviation, each in
// its own frame: T in a double hexagon (Trench Throne), S in a triangle
// (Relay Spire), V in a chimney pentagon (Forge Vent), K in a rounded plot
// (Kelp Terrace), B in a heavy square (Deep Battery), D in a circle (Drydock
// Shoal), A in a diamond (Archive Vault), M in an octagon (Marshal Court) and
// W in a ring of dots (Coral Ward).
//
// **There is no text here.** The card writes the letter with a font, and
// `MarkGraphics` cannot. The label lane of the hold marks lays out a glyph
// run per frame, and the site layer does not carry it for a control point, so
// each of the nine letters is line geometry: `LETTERS` below states every
// letter as stroked polylines on one cap height and one baseline, so the nine
// share one weight. A curved bowl is an elliptical arc, sampled into the same
// point list. The card knocks the letter out of the frame with a wide stroke
// in the water's dark; this file draws that halo the same way.
//
// The contested tell: two crossed swords in the attacker's ink stand behind
// the letter. The card lets them glow with the beacon blink; the atlas bakes
// the still mark (R1104), so they stand at the middle of that blink.
//
// Every offset, weight, radius and alpha below is the figure card 18 draws.

import type { MarkGraphics } from '../marks';
import {
  BLINK, disc, ellipseArc, line, regular, roundRectPoints, stroke,
  WATER, setOf, type StructureDraw, type StructureInk, type StructureKind,
  type StructureSet,
} from './shared';

/** The cap height of the letters, and the weight the nine share. */
const CAP = 5.5;
const LETTER_WIDTH = 2;
/** The knock-out behind a letter, wide enough to clear the frame line. */
const HALO_WIDTH = 4.8;

/**
 * The nine letters, each as a list of stroked polylines.
 *
 * The cap box runs from `-CAP` to `CAP` in y, and about the same half width
 * in x, which is the proportion of the bold monospaced letter the card sets.
 * A round letter states its bowl as an elliptical arc of the same box, so B,
 * D and S keep the height of T and M.
 */
const LETTERS: Record<StructureKind, number[][]> = {
  // T: the arm over the stem.
  throne: [[-3.5, -CAP, 3.5, -CAP], [0, -CAP, 0, CAP]],
  // S: the upper bowl and the lower bowl, as one run.
  spire: [(() => {
    const pts: number[] = [];
    ellipseArc(pts, 0, -2.75, 3.3, 2.75, -0.63, -Math.PI * 1.5);
    ellipseArc(pts, 0, 2.75, 3.3, 2.75, -Math.PI / 2, 2.51);
    return pts;
  })()],
  // V: the two legs.
  forge: [[-3.6, -CAP, 0, CAP, 3.6, -CAP]],
  // K: the stem and the two arms, which branch off one point of the stem.
  kelp: [
    [-3.2, -CAP, -3.2, CAP],
    [3.4, -CAP, -3.2, 0.4],
    [-1.5, -1.12, 3.4, CAP],
  ],
  // B: the stem, the upper bowl and the lower bowl.
  battery: [
    [-3, -CAP, -3, CAP],
    (() => {
      const pts = [-3, -CAP, 0.4, -CAP];
      ellipseArc(pts, 0.4, -2.75, 2.9, 2.75, -Math.PI / 2, Math.PI / 2);
      pts.push(-3, 0);
      return pts;
    })(),
    (() => {
      const pts = [-3, 0, 0.4, 0];
      ellipseArc(pts, 0.4, 2.75, 3.1, 2.75, -Math.PI / 2, Math.PI / 2);
      pts.push(-3, CAP);
      return pts;
    })(),
  ],
  // D: the stem and the one bowl of the whole cap height.
  drydock: [
    [-3.2, -CAP, -3.2, CAP],
    (() => {
      const pts = [-3.2, -CAP, 0, -CAP];
      ellipseArc(pts, 0, 0, 3.6, CAP, -Math.PI / 2, Math.PI / 2);
      pts.push(-3.2, CAP);
      return pts;
    })(),
  ],
  // A: the two legs and the crossbar, which meets each leg on the leg.
  archive: [[-3.6, CAP, 0, -CAP, 3.6, CAP], [-2.23, 1.3, 2.23, 1.3]],
  // M: the two stems and the valley between them.
  marshal: [[-3.5, CAP, -3.5, -CAP, 0, 1.4, 3.5, -CAP, 3.5, CAP]],
  // W: the two stems and the peak between them.
  ward: [[-3.8, -CAP, -2.1, CAP, 0, -1.4, 2.1, CAP, 3.8, -CAP]],
};

/** How far down the frame carries the letter. The card lifts two frames. */
const LETTER_DY: Partial<Record<StructureKind, number>> = {
  spire: 5,
  forge: 2,
};

/**
 * The frame of one kind.
 *
 * The card draws each frame two times: once filled at a low alpha, under the
 * swords, and once stroked, over them. `ink` is the holder's colour in both
 * passes, and `outline` says which pass this is.
 */
function frame(
  g: MarkGraphics, kind: StructureKind, color: number,
  alpha: number, fillAlpha: number, outline: boolean,
): void {
  const paint = (pts: number[], width: number): void => {
    if (outline) {
      g.poly(pts);
      stroke(g, color, alpha, width);
    } else {
      g.poly(pts).fill({ color, alpha: fillAlpha });
    }
  };
  switch (kind) {
    case 'throne':
      // The double hexagon of the capital.
      paint(regular(0, 0, 16, 6, -Math.PI / 2), 1.4);
      if (outline) paint(regular(0, 0, 12.5, 6, -Math.PI / 2), 1);
      break;
    case 'spire':
      paint(regular(0, 3, 16, 3, -Math.PI / 2), 1.4);
      break;
    case 'forge':
      // The chimney pentagon.
      paint([0, -15, 12, -5, 12, 12, -12, 12, -12, -5], 1.4);
      break;
    case 'kelp':
      paint(roundRectPoints(-14, -10, 28, 20, 5), 1.4);
      break;
    case 'battery':
      // The heavy square.
      paint([-12, -12, 12, -12, 12, 12, -12, 12], 2.4);
      break;
    case 'drydock':
      if (outline) g.circle(0, 0, 13).stroke({ width: 1.4, color, alpha });
      else g.circle(0, 0, 13).fill({ color, alpha: fillAlpha });
      break;
    case 'archive':
      paint(regular(0, 0, 16, 4, 0), 1.4);
      break;
    case 'marshal':
      paint(regular(0, 0, 14, 8, Math.PI / 8), 1.4);
      break;
    case 'ward':
      // The ring of dots. The fill pass is the whole disc under them.
      if (outline) {
        for (let k = 0; k < 12; k++) {
          const a = (k * Math.PI) / 6;
          disc(g, Math.cos(a) * 13.5, Math.sin(a) * 13.5, 1.5, color, alpha);
        }
      } else {
        g.circle(0, 0, 13.5).fill({ color, alpha: fillAlpha });
      }
      break;
  }
}

/**
 * Two crossed swords behind the letter, in the attacker's ink.
 *
 * Each sword is a wide soft blade under a thin bright one, with the guard
 * across the hilt and the pommel at its end.
 */
function swords(g: MarkGraphics, foe: number): void {
  for (const sx of [-1, 1]) {
    const x0 = -15 * sx, y0 = -15, x1 = 15 * sx, y1 = 15;
    line(g, x0, y0, x1, y1, foe, 0.3 * BLINK, 4.5);
    line(g, x0, y0, x1, y1, foe, 0.55 + 0.45 * BLINK, 1.6);
    const gx = x1 - 4.5 * sx, gy = y1 - 4.5;
    line(g, gx - 2.6, gy + 2.6 * sx, gx + 2.6, gy - 2.6 * sx, foe, 0.95, 1.8);
    disc(g, x1, y1, 1.6, foe, 0.95);
  }
}

/** One kind of the `letters` set, still. */
function markOf(kind: StructureKind): StructureDraw {
  // The card sets the letter one pixel under the middle of its frame.
  const dy = (LETTER_DY[kind] ?? 0) + 1;
  const runs = LETTERS[kind].map((pts) => {
    const out: number[] = [];
    for (let i = 0; i < pts.length; i += 2) out.push(pts[i], pts[i + 1] + dy);
    return out;
  });
  return (g: MarkGraphics, ink: StructureInk): void => {
    const a = ink.held ? 0.95 : 0.7;
    const fa = ink.held ? 0.14 : 0.05;
    frame(g, kind, ink.own, a, fa, false);
    if (ink.foe !== 0) swords(g, ink.foe);
    frame(g, kind, ink.own, a, fa, true);
    // The knock-out first, so the letter stands clear of the frame and of
    // the swords, and then the letter itself.
    for (const run of runs) {
      g.poly(run, false);
      stroke(g, WATER, 0.95, HALO_WIDTH);
    }
    for (const run of runs) {
      g.poly(run, false);
      stroke(g, ink.own, a, LETTER_WIDTH);
    }
  };
}

/** Card 18, all nine kinds. */
export const LETTERS_SET: StructureSet = setOf(markOf);
